/**
 * Tutorial Step Definitions
 *
 * Contains all step definitions for the complete first-time user journey:
 * 1. Lobby - Create/Join game options
 * 2. Create Game - Avatar, team name, game mode
 * 3. Waiting Room - Room code, players, starting
 * 4. Game - Full game interface
 */

import type { Tutorial, TutorialId } from '@/types/tutorial'

/**
 * Lobby Tutorial - Initial landing page
 * Flow: Welcome → Explain all elements → Direct to action
 */
export const lobbyTutorial: Tutorial = {
  id: 'lobby',
  name: 'Lobby Guide',
  description: 'Learn how to create or join a game',
  estimatedDuration: 45,
  steps: [
    {
      id: 'welcome',
      target: null,
      title: 'Welcome to Canasta!',
      description:
        "Let's take a quick tour of the lobby. We'll show you around, then help you create your first game.",
      position: 'center',
      icon: 'waving_hand'
    },
    {
      id: 'create-game-explain',
      target: {
        selector: '[data-tutorial="create-game"]',
        fallbackSelector: '.mode-card:first-child'
      },
      title: 'Create a Game',
      description:
        'This button lets you start a new game. You can play with **AI bots** immediately, or create a room and invite **friends** to join.',
      position: 'bottom',
      icon: 'add_circle',
      spotlightPadding: 8
    },
    {
      id: 'join-game-explain',
      target: {
        selector: '[data-tutorial="join-game"]',
        fallbackSelector: '.mode-card:last-child'
      },
      title: 'Join a Game',
      description:
        "Have a room code from a friend? Use this to enter their game. You'll need the 6-character code they share with you.",
      position: 'bottom',
      icon: 'group_add',
      spotlightPadding: 8
    },
    {
      id: 'recent-games',
      target: {
        selector: '[data-tutorial="recent-games"]',
        fallbackSelector: '.recent-games-section, .games-section'
      },
      title: 'Your Games',
      description:
        'Any games in progress will appear here. You can click to resume where you left off.',
      position: 'top',
      icon: 'history',
      spotlightPadding: 8
    },
    {
      id: 'lobby-menu',
      target: {
        selector: '[data-tutorial="lobby-menu"]',
        fallbackSelector: '.lobby-menu-button'
      },
      title: 'Menu',
      description:
        'Access game rules via **How to Play**, change settings, or return to this guide anytime.',
      position: 'bottom-left',
      icon: 'more_vert',
      spotlightPadding: 4
    },
    {
      id: 'create-game-action',
      target: {
        selector: '[data-tutorial="create-game"]',
        fallbackSelector: '.mode-card:first-child'
      },
      title: "Let's Create a Game!",
      description:
        "Now that you know your way around, click **Create Game** to set up your first game!",
      position: 'bottom',
      icon: 'play_arrow',
      spotlightPadding: 8,
      allowInteraction: true,
      requiresAction: true
    }
  ]
}

/**
 * Create Game Tutorial - Game setup screen
 * Flow: Intro → Explain all options → Direct to action
 */
export const createGameTutorial: Tutorial = {
  id: 'create-game',
  name: 'Game Setup Guide',
  description: 'Customize your game settings',
  estimatedDuration: 60,
  steps: [
    {
      id: 'create-intro',
      target: null,
      title: 'Game Setup',
      description:
        "This is where you customize your game. Let's look at your options, then you can set things up how you like.",
      position: 'center',
      icon: 'tune'
    },
    {
      id: 'avatar-selection',
      target: {
        selector: '[data-tutorial="avatar-carousel"]',
        fallbackSelector: '.avatar-carousel'
      },
      title: 'Your Avatar',
      description:
        'This is how other players will see you. Use the **arrows** to browse, or click **Random** for a surprise! Your choice is saved for future games.',
      position: 'bottom',
      icon: 'face',
      spotlightPadding: 12
    },
    {
      id: 'avatar-try-it',
      target: {
        selector: '[data-tutorial="avatar-carousel"]',
        fallbackSelector: '.avatar-carousel'
      },
      title: 'Try it!',
      description: 'Select your avatar now!',
      position: 'bottom',
      icon: 'touch_app',
      spotlightPadding: 12
    },
    {
      id: 'team-name',
      target: {
        selector: '[data-tutorial="team-name"]',
        fallbackSelector: '.team-name-input'
      },
      title: 'Team Name',
      description:
        'In Canasta, you play with a teammate. Give your team a custom name such as **Alissa / Lexi**, or click the **dice** to generate a fun random one!',
      position: 'bottom',
      icon: 'group',
      spotlightPadding: 8
    },
    {
      id: 'team-name-try-it',
      target: {
        selector: '[data-tutorial="team-name"]',
        fallbackSelector: '.team-name-input'
      },
      title: 'Try it!',
      description: 'Type in a team name now!',
      position: 'bottom',
      icon: 'edit',
      spotlightPadding: 8
    },
    {
      id: 'game-mode',
      target: {
        selector: '[data-tutorial="game-mode"]',
        fallbackSelector: '.game-mode-cards'
      },
      title: 'Game Mode',
      description:
        'Two ways to play: **Play with Bots** starts immediately with AI opponents. **Wait for Friends** creates a room code you can share.',
      position: 'top',
      icon: 'sports_esports',
      spotlightPadding: 8
    },
    {
      id: 'start-button-explain',
      target: {
        selector: '[data-tutorial="start-game-btn"]',
        fallbackSelector: '.btn-create-game, .form-actions .btn-primary'
      },
      title: 'Create Game Button',
      description:
        'Once you\'re happy with your settings, this button creates your game. For bot games, you\'ll start playing immediately!',
      position: 'top',
      icon: 'rocket_launch',
      spotlightPadding: 8
    },
    {
      id: 'start-button-action',
      target: {
        selector: '[data-tutorial="start-game-btn"]',
        fallbackSelector: '.btn-create-game, .form-actions .btn-primary'
      },
      title: 'Ready to Play!',
      description:
        "Pick your avatar, name your team, select **Play with Bots**, then click **Create Game** to start!",
      position: 'top',
      icon: 'play_arrow',
      spotlightPadding: 8,
      allowInteraction: true,
      requiresAction: true
    }
  ]
}

/**
 * Waiting Room Tutorial - For multiplayer games
 */
export const waitingRoomTutorial: Tutorial = {
  id: 'waiting-room',
  name: 'Waiting Room Guide',
  description: 'Learn how to invite friends and start',
  estimatedDuration: 30,
  steps: [
    {
      id: 'waiting-intro',
      target: null,
      title: 'Waiting for Players',
      description:
        "Your game room is ready! Share the room code with friends or add bots to fill empty seats.",
      position: 'center',
      icon: 'group'
    },
    {
      id: 'room-code',
      target: {
        selector: '[data-tutorial="room-code"]',
        fallbackSelector: '.room-code-large, .room-code-share'
      },
      title: 'Room Code',
      description:
        'Share this code with friends so they can join. Click the code or **Copy** button to copy it to your clipboard.',
      position: 'bottom',
      icon: 'content_copy',
      spotlightPadding: 8
    },
    {
      id: 'player-slots',
      target: {
        selector: '[data-tutorial="player-slots"]',
        fallbackSelector: '.player-slots, .players-section'
      },
      title: 'Player Seats',
      description:
        'Players are organized into two teams. You can **Add Bots** to fill empty seats, or wait for friends to join.',
      position: 'bottom',
      icon: 'event_seat',
      spotlightPadding: 8
    },
    {
      id: 'start-game',
      target: {
        selector: '[data-tutorial="start-game"]',
        fallbackSelector: '.waiting-actions .btn-primary'
      },
      title: 'Start the Game',
      description:
        "Once all 4 seats are filled, click **Start Game** to begin! You're the host, so only you can start.",
      position: 'top',
      icon: 'play_arrow',
      spotlightPadding: 8,
      allowInteraction: true,
      requiresAction: true
    }
  ]
}

/**
 * Game Tutorial - How to play the game interface
 * Flow: Welcome → Board Layout → Card Mechanics → Canasta Types → Playing Cards → Summary
 */
export const gameTutorial: Tutorial = {
  id: 'game',
  name: 'Game Interface Guide',
  description: 'Learn the game board controls and layout',
  estimatedDuration: 300,
  steps: [
    // ========== START ROUND ==========
    {
      id: 'start-round',
      target: {
        selector: '[data-tutorial="start-round-btn"]',
        fallbackSelector: '.rocket-button'
      },
      title: 'Start the Round!',
      description:
        "When you're ready, click the **Start Round** button to deal the cards and begin playing. The tutorial will continue once the round starts.",
      position: 'bottom',
      icon: 'rocket_launch',
      spotlightPadding: 16,
      allowInteraction: true,
      requiresAction: true
    },

    // ========== WELCOME ==========
    {
      id: 'game-welcome',
      target: null,
      title: "Let's Play Canasta!",
      description:
        "Welcome to the game board! This guide will teach you everything you need to know. Let's start by looking around.",
      position: 'center',
      icon: 'sports_esports'
    },

    // ========== BOARD LAYOUT ==========
    {
      id: 'player-hand',
      target: {
        selector: '[data-tutorial="player-hand"]',
        fallbackSelector: '.player-hand, .hand-scroll-container'
      },
      title: 'Your Hand',
      description:
        'These are your cards. They\'re automatically sorted by rank to help you spot matches and plan your melds.',
      position: 'top',
      icon: 'playing_cards',
      spotlightPadding: 8
    },
    {
      id: 'hand-foot-indicator',
      target: {
        selector: '[data-tutorial="foot-indicator"]',
        fallbackSelector: '.player-stats-badge, .foot-indicator'
      },
      title: 'Hand & Foot System',
      description:
        'In this variant, you have TWO piles of cards: your **Hand** (First Hand) and your **Foot** (Second Hand). The dots show which pile you\'re on. Empty your Hand to pick up your Foot!',
      position: 'top-right',
      icon: 'looks_two',
      spotlightPadding: 8
    },
    {
      id: 'turn-indicator',
      target: {
        selector: '[data-tutorial="turn-indicator"]',
        fallbackSelector: '.turn-indicator, .current-turn'
      },
      title: 'Turn Indicator',
      description:
        'This shows whose turn it is. When it\'s your turn, a dot will appear to the right of your name and your avatar will highlight with a green circle.',
      position: 'left',
      icon: 'person',
      spotlightPadding: 8
    },
    {
      id: 'draw-pile',
      target: {
        selector: '[data-tutorial="draw-pile"]',
        fallbackSelector: '#stock-pile, .draw-pile'
      },
      title: 'Draw Pile',
      description:
        'The deck of cards. When it\'s your turn, a **Draw Cards** button will appear over your hand. Click the button or click directly on the draw pile to **draw 2 cards**. You must draw before melding or discarding.',
      position: 'right',
      icon: 'stack',
      spotlightPadding: 8
    },
    {
      id: 'discard-pile',
      target: {
        selector: '[data-tutorial="discard-pile"]',
        fallbackSelector: '#discard-pile, .discard-pile'
      },
      title: 'Discard Pile',
      description:
        'Cards that have been discarded. Instead of drawing, you can **pick up the entire pile** if you have 2 natural cards matching the top card!',
      position: 'left',
      icon: 'layers',
      spotlightPadding: 8
    },
    {
      id: 'frozen-pile',
      target: null,
      title: 'Frozen Pile',
      description:
        'When a **black 3** or **red 3** is discarded, the pile becomes **frozen** and cannot be picked up. Wild cards (2s and Jokers) on top can still be picked up if you have **2 matching wilds** in your hand, just like any other card!',
      position: 'center',
      icon: 'ac_unit'
    },
    {
      id: 'teammate-video',
      target: {
        selector: '[data-tutorial="teammate-video"]',
        dataAttribute: 'teammate-video',
        fallbackSelector: '#video-seat-2'
      },
      title: 'Your Teammate',
      description:
        'This is your **teammate\'s** video feed (if their camera is enabled). Below their video you\'ll see their **card count** and **Hand/Foot status dots** so you know how close they are to finishing!',
      position: 'bottom-right',
      icon: 'group',
      spotlightPadding: 12
    },
    {
      id: 'team-melds',
      target: {
        selector: '[data-tutorial="team-melds"]',
        fallbackSelector: '.my-canastas-section, #my-canastas-section'
      },
      title: "Your Team's Melds",
      description:
        'When you meld cards (3+ of same rank), they appear here. Build melds to **7 cards** to complete a Canasta!\n\nYour first meld each round must meet a point threshold based on your score:\n• 0-2,500: **50 points**\n• 2,501-5,000: **90 points**\n• 5,001-7,500: **120 points**\n• 7,500+: **150 points**\n\nAfter your team\'s first meld is played, you can play freely!',
      position: 'top-right',
      icon: 'category',
      spotlightPadding: 8
    },
    {
      id: 'opponent-melds',
      target: {
        selector: '[data-tutorial="opponent-melds"]',
        fallbackSelector: '.opponent-canastas-section, #opponent-canastas-section'
      },
      title: "Opponent's Melds",
      description:
        "Keep an eye on the other team's progress. They're racing to complete Canastas too!",
      position: 'bottom',
      icon: 'visibility',
      spotlightPadding: 8
    },
    {
      id: 'scoreboard',
      target: {
        selector: '#game-sidebar',
        dataAttribute: 'scoreboard',
        fallbackSelector: '.game-sidebar'
      },
      title: 'Game Activity & Scores',
      description:
        'This panel shows the **Game Activity** log (recent plays and events) and each team\'s **score**. First team to reach **10,000 points** wins the game!',
      position: 'left',
      icon: 'leaderboard',
      spotlightPadding: 0
    },
    {
      id: 'game-room-code',
      target: {
        selector: '[data-tutorial="game-room-code"]',
        fallbackSelector: '.room-code-btn, #game-room-code-display'
      },
      title: 'Share with Friends',
      description:
        'Want to invite a friend mid-game? **Click this button** to copy the room code to your clipboard. Share it with friends so they can join using the "Join Game" option from the main menu!',
      position: 'left',
      icon: 'share',
      spotlightPadding: 8
    },
    {
      id: 'opening-meld',
      target: null,
      title: 'Opening Meld Requirement',
      description:
        'Your first meld each round must meet a **point threshold** based on your score:\n\n• **0-1,499**: 50 points\n• **1,500-2,999**: 90 points\n• **3,000-4,999**: 120 points\n• **5,000+**: 150 points\n\nAfter your team\'s first meld, you can play freely!',
      position: 'center',
      icon: 'toll'
    },

    // ========== SPECIAL CARDS ==========
    {
      id: 'wild-cards',
      target: null,
      title: 'Wild Cards',
      description:
        '**2s** and **Jokers** are wild! You can create a **wild canasta** with just wilds by selecting wild cards together. Once you play a wild canasta, you will be given the option to play a **hidden canasta**.\n\nHidden canastas are worth more points, but don\'t get caught with them in your hand!',
      position: 'center',
      icon: 'auto_awesome'
    },
    {
      id: 'red-threes',
      target: null,
      title: "Red 3's",
      description:
        "**Red 3s are bad!** Each Red 3 is **-500 points** which makes this Hooley style. Make sure to discard those red 3's as quick as possible and don't get caught with your second hand, it may have red 3's!",
      position: 'center',
      icon: 'local_fire_department'
    },

    // ========== CANASTA TYPES ==========
    {
      id: 'canasta-intro',
      target: null,
      title: 'Understanding Canastas',
      description:
        'A **Canasta** is a meld of 7+ cards. There are 4 types, each worth different bonus points. Let\'s learn about each one!',
      position: 'center',
      icon: 'stars'
    },
    {
      id: 'natural-canasta',
      target: null,
      title: 'Natural (Clean) Canasta',
      description:
        'A **Natural Canasta** contains **NO wild cards** - only natural cards of the same rank. Worth **500 bonus points**!',
      position: 'center',
      icon: 'verified'
    },
    {
      id: 'mixed-canasta',
      target: null,
      title: 'Mixed (Dirty) Canasta',
      description:
        'A **Mixed Canasta** contains **1-3 wild cards** mixed with natural cards. Worth **300 bonus points**.',
      position: 'center',
      icon: 'contrast'
    },
    {
      id: 'wild-canasta',
      target: null,
      title: 'Wild Canasta',
      description:
        'A **Wild Canasta** is made entirely of **wild cards** (2s and Jokers). Very rare and worth **1,000 bonus points**! You need 7 wild cards to complete one.',
      position: 'center',
      icon: 'bolt'
    },
    {
      id: 'hidden-canasta',
      target: null,
      title: 'Hidden Canasta',
      description:
        "A **Hidden Canasta** is a canasta your teammate can't see. You hold it in your hand until the canasta is completed - you meld all 7+ from your own hand! Worth an extra **1500 points**.",
      position: 'center',
      icon: 'mystery'
    },
    {
      id: 'clean-dirty-tracker',
      target: {
        selector: '[data-tutorial="clean-dirty-indicator"]',
        dataAttribute: 'clean-dirty-indicator',
        fallbackSelector: '#canasta-requirements'
      },
      title: 'Clean/Dirty Indicators',
      description:
        'Each meld shows colored circles: **Red** = Clean (no wilds), **Black** = Dirty (has wilds). This helps you quickly see which melds can become Natural vs Mixed Canastas!',
      position: 'top-right',
      icon: 'radio_button_checked',
      spotlightPadding: 8
    },

    // ========== PLAYING CARDS ==========
    {
      id: 'selecting-cards',
      target: {
        selector: '[data-tutorial="player-hand"]',
        fallbackSelector: '.player-hand'
      },
      title: 'Selecting Cards',
      description:
        '**Click** a card to select it (it will rise up). Click again to deselect. Select multiple cards to meld them together or to discard.',
      position: 'top',
      icon: 'touch_app',
      spotlightPadding: 8
    },
    {
      id: 'playing-to-melds',
      target: {
        selector: '[data-tutorial="team-melds"]',
        fallbackSelector: '.my-canastas-section'
      },
      title: 'Adding to Melds',
      description:
        'When you select a card, compatible melds will **glow** to show you can play on them! You can either:\n\n• **Click the glowing meld** to add your card\n• **Drag the card** directly onto the meld\n• Use the **Play Canasta button** to create a new meld',
      position: 'top',
      icon: 'add_circle',
      spotlightPadding: 8
    },
    {
      id: 'meld-highlighting',
      target: null,
      title: 'Meld Highlighting',
      description:
        'When you select cards, the game helps you:\n\n• **Glowing melds** = You can add your selected card here\n• **Meld button enabled** = You can create a new meld\n• **Tooltips** = Explain why actions are disabled',
      position: 'center',
      icon: 'lightbulb'
    },

    // ========== ACTIONS & GOING OUT ==========
    {
      id: 'action-buttons',
      target: null,
      title: 'Action Buttons',
      description:
        '• **Draw**: Take 2 cards from the deck (top left)\n• **Pickup**: Take the discard pile (top left)\n• **Play Canasta**: Play selected cards as a new meld\n• **Discard**: End your turn by discarding 1 card',
      position: 'center',
      icon: 'gamepad'
    },
    {
      id: 'going-out',
      target: null,
      title: 'Going Out',
      description:
        'To end the round, you must **go out** by playing all your cards. Requirements:\n\n• Your team must have at least **4 Canastas** (2 Clean and 2 Dirty)\n• You must be in your **Foot** (second hand)\n• End with a discard or play on a canasta\n\nThe team that goes out gets **100 bonus points**!',
      position: 'center',
      icon: 'exit_to_app'
    },

    // ========== MENU & SUMMARY ==========
    {
      id: 'game-menu',
      target: {
        selector: '[data-tutorial="game-menu"]',
        fallbackSelector: '.game-menu-button'
      },
      title: 'Game Menu',
      description:
        'Access game rules, settings, and this guide anytime. You can also leave the game or report issues from here.',
      position: 'bottom-left',
      icon: 'more_vert',
      spotlightPadding: 4
    },
    {
      id: 'turn-summary',
      target: null,
      title: 'Your Turn - Quick Reference',
      description:
        'Each turn has 3 phases:\n\n**1. Draw** - Take 2 cards (or pickup pile)\n**2. Meld** - Optionally play cards to melds\n**3. Discard** - End turn by discarding 1 card\n\n**Goal:** Complete Canastas, then go out!\n\nGood luck and have fun!',
      position: 'center',
      icon: 'checklist'
    }
  ]
}

/**
 * All tutorials registry
 */
export const tutorials: Record<TutorialId, Tutorial> = {
  'lobby': lobbyTutorial,
  'create-game': createGameTutorial,
  'waiting-room': waitingRoomTutorial,
  'game': gameTutorial
}
